/**
 * Structured renderer for a completed analysis report (SA-212). Section
 * order and empty-section omission mirror the server's deterministic
 * markdown render (services/conversation_analysis/render.py). Unknown
 * top-level string keys render as generic prose sections; a report that
 * fails reportHasCoreShape is the caller's cue to fall back to
 * report_markdown (AnalysisRunPage).
 */
import type { ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { Badge, Button } from '@/components/ui'
import { usePermission } from '@/hooks/usePermission'
import { cn } from '@/utils'
import type {
  AnalysisFailureCluster,
  AnalysisFunnelStats,
  AnalysisMissionMeasuresStats,
  AnalysisReport,
  AnalysisRunDetail,
} from '@/types/conversationAnalysis'

const KNOWN_REPORT_KEYS = new Set([
  'executive_summary',
  'outcome_breakdown',
  'failure_clusters',
  'escalation_patterns',
  'comparisons',
  'recommendations',
])

const OUTCOME_COLORS: Record<string, string> = {
  successful: 'bg-success',
  unsuccessful: 'bg-destructive',
  inconclusive: 'bg-muted-foreground',
}

export function reportHasCoreShape(
  report: AnalysisReport | null,
): report is AnalysisReport {
  return (
    !!report &&
    typeof report.executive_summary === 'string' &&
    (report.failure_clusters === undefined || Array.isArray(report.failure_clusters))
  )
}

function titleize(key: string): string {
  return key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2 print:break-inside-avoid">
      <h2 className="text-sm font-semibold text-foreground">{title}</h2>
      {children}
    </section>
  )
}

/** InboxDashboard ActivityBar idiom, incl. the nonzero-visibility floor. */
function BreakdownBar({
  label,
  value,
  maxValue,
  color,
}: {
  label: string
  value: number
  maxValue: number
  color: string
}) {
  const pct = maxValue > 0 ? (value / maxValue) * 100 : 0
  return (
    <div className="flex items-center gap-3">
      <span className="w-36 text-xs text-muted-foreground text-right shrink-0 truncate">
        {label}
      </span>
      <div className="flex-1 bg-muted rounded-full h-4 overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all', color)}
          style={{ width: `${Math.max(pct, value > 0 ? 2 : 0)}%` }}
        />
      </div>
      <span className="w-8 text-xs text-muted-foreground text-right shrink-0">{value}</span>
    </div>
  )
}

function BreakdownBars({
  data,
  colorFor,
}: {
  data: Record<string, number>
  colorFor: (key: string) => string
}) {
  const entries = Object.entries(data)
  if (entries.length === 0) return null
  const max = Math.max(...entries.map(([, v]) => v))
  return (
    <div className="space-y-1">
      {entries.map(([key, value]) => (
        <BreakdownBar
          key={key}
          label={key.replace(/_/g, ' ')}
          value={value}
          maxValue={max}
          color={colorFor(key)}
        />
      ))}
    </div>
  )
}

/** Deterministic SA-236 funnel tables — mirrors render.py's markdown
 * section; absent stats.funnel (pre-SA-236 runs) renders nothing. */
function FunnelSection({ funnel }: { funnel: AnalysisFunnelStats }) {
  const driveBy = funnel.drive_by ?? {}
  const stages = Array.isArray(funnel.stages) ? funnel.stages : []
  const diedEntries = Object.entries(funnel.died_awaiting ?? {})
  const handoff = funnel.handoff_followup ?? {}
  return (
    <Section title="Order funnel">
      <p className="text-sm text-muted-foreground">
        {funnel.total ?? 0} conversations: {funnel.no_workflow ?? 0} without an order
        workflow, {driveBy.total ?? 0} drive-by ({driveBy.ad_attributed ?? 0} ad-attributed),{' '}
        {funnel.engaged ?? 0} engaged. Percentages are over the engaged cohort — drive-by
        conversations (ad-click pattern, never engaged after the greeting) are excluded.
      </p>
      {stages.length > 0 && (
        <table className="w-full max-w-md text-sm">
          <thead>
            <tr className="text-left text-xs text-muted-foreground">
              <th className="py-1 font-medium">Stage</th>
              <th className="py-1 text-right font-medium">Reached</th>
              <th className="py-1 text-right font-medium">% of engaged</th>
            </tr>
          </thead>
          <tbody>
            {stages.map((stage) => (
              <tr key={stage.stage} className="border-t border-border">
                <td className="py-1 text-foreground">{stage.stage}</td>
                <td className="py-1 text-right text-foreground">{stage.reached}</td>
                <td className="py-1 text-right text-foreground">{stage.pct_of_engaged}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {diedEntries.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Died awaiting:{' '}
          {diedEntries.map(([slot, n]) => `${slot.replace(/_/g, ' ')} (${n})`).join(', ')}
        </p>
      )}
      {(handoff.handoffs ?? 0) > 0 && (
        <p className="text-xs text-muted-foreground">
          Handoffs: {handoff.handoffs} — {handoff.followed_up ?? 0} followed up
          {typeof handoff.median_minutes === 'number'
            ? ` (median ${handoff.median_minutes} min)`
            : ''}
          , {handoff.no_followup ?? 0} without follow-up.
        </p>
      )}
    </Section>
  )
}

/** Deterministic SA-285 mission rollup — mirrors render.py's markdown
 * section; absent stats.mission_measures renders nothing. Dimensions are
 * queue states + waiting_on roles only (no per-staff data by design). */
const WAIT_BY_OWNER_ORDER = ['customer', 'commercial', 'specialist', 'dispatch'] as const

function MissionMeasuresSection({ block }: { block: AnalysisMissionMeasuresStats }) {
  const med = (d?: { n?: number; median?: number | null }) =>
    typeof d?.median === 'number' ? `${d.median} min (n=${d.n ?? 0})` : `n/a (n=${d?.n ?? 0})`
  // render.py sorts these breakdown dicts alphabetically (sorted(...items()))
  // — the backend builds them in QUEUE_STATES/WAITING_ON_VALUES tuple order,
  // so plain object-key order would silently diverge from the markdown twin.
  const byState = Object.entries(block.by_queue_state ?? {})
    .filter(([, v]) => v)
    .sort(([a], [b]) => a.localeCompare(b))
  const byOwner = Object.entries(block.by_waiting_on ?? {})
    .filter(([, v]) => v)
    .sort(([a], [b]) => a.localeCompare(b))
  const waitsByOwner = block.wait_by_owner_minutes ?? {}
  // render.py:110-120 — the table renders whenever ANY owner has a nonzero
  // total, but always lists all four fixed owners (zero-total rows show
  // too) rather than filtering rows out.
  const showWaits = Object.values(waitsByOwner).some((v) => (v?.total ?? 0) > 0)
  const buckets = block.trend?.buckets ?? []
  const openAge = block.end_to_end_minutes?.open?.median_age
  return (
    <Section title="Order measures">
      <p className="text-sm text-muted-foreground">
        {block.total ?? 0} quote-class orders in window: {block.open ?? 0} open,{' '}
        {block.closed ?? 0} closed.
      </p>
      {byState.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Open by queue state:{' '}
          {byState.map(([k, v]) => `${k.replace(/_/g, ' ')} (${v})`).join(', ')}
        </p>
      )}
      {byOwner.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Waiting on: {byOwner.map(([k, v]) => `${k} (${v})`).join(', ')}
        </p>
      )}
      <p className="text-sm text-muted-foreground">
        End-to-end (closed): {med(block.end_to_end_minutes?.closed)}; open
        orders: {block.end_to_end_minutes?.open?.n ?? 0}
        {typeof openAge === 'number' ? ` (median age ${openAge} min)` : ''}.
        Touch time: {med(block.touch_time_minutes)} —{' '}
        {block.touch_time_minutes?.method ?? 'approximated from staff activity'}.
        Revision response: {med(block.revision_response_minutes)}; decision
        latency: {med(block.decision_latency_minutes)} (
        {block.decision_latency_minutes?.open ?? 0} open).
      </p>
      {showWaits && (
        <table className="w-full max-w-md text-sm">
          <thead>
            <tr className="text-left text-xs text-muted-foreground">
              <th className="py-1 font-medium">Waiting on</th>
              <th className="py-1 text-right font-medium">Orders</th>
              <th className="py-1 text-right font-medium">Median min</th>
              <th className="py-1 text-right font-medium">Total min</th>
            </tr>
          </thead>
          <tbody>
            {WAIT_BY_OWNER_ORDER.map((owner) => {
              const row = waitsByOwner[owner]
              return (
                <tr key={owner} className="border-t border-border">
                  <td className="py-1 text-foreground">{owner}</td>
                  <td className="py-1 text-right text-foreground">{row?.n ?? 0}</td>
                  <td className="py-1 text-right text-foreground">
                    {typeof row?.median === 'number' ? row.median : '—'}
                  </td>
                  <td className="py-1 text-right text-foreground">{row?.total ?? 0}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
      {buckets.some((b) => (b.total ?? 0) > 0) && (
        <table className="w-full max-w-md text-sm">
          <thead>
            <tr className="text-left text-xs text-muted-foreground">
              <th className="py-1 font-medium">Week of</th>
              <th className="py-1 text-right font-medium">Orders</th>
              <th className="py-1 text-right font-medium">Closed</th>
              <th className="py-1 text-right font-medium">E2E median</th>
              <th className="py-1 text-right font-medium">Touch median</th>
            </tr>
          </thead>
          <tbody>
            {buckets.map((b) => (
              <tr key={b.start} className="border-t border-border">
                <td className="py-1 text-foreground">{b.start}</td>
                <td className="py-1 text-right text-foreground">{b.total ?? 0}</td>
                <td className="py-1 text-right text-foreground">{b.closed ?? 0}</td>
                <td className="py-1 text-right text-foreground">
                  {typeof b.end_to_end_closed_median === 'number'
                    ? b.end_to_end_closed_median : '—'}
                </td>
                <td className="py-1 text-right text-foreground">
                  {typeof b.touch_time_median === 'number'
                    ? b.touch_time_median : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="text-xs text-muted-foreground">
        Touch time is approximated from staff activity (sessionized), never a
        tracked timer. Open-order ages are censored and never mixed into
        closed medians.
      </p>
    </Section>
  )
}

function FailureClusterCard({
  cluster,
  inboxEnabled,
}: {
  cluster: AnalysisFailureCluster
  inboxEnabled: boolean
}) {
  const navigate = useNavigate()
  // SA-237: pre-fill the deep-dive builder with this cluster's examples.
  const canRun = usePermission('conversation_analysis:run')
  return (
    <div className="rounded-sm border border-border bg-card p-3 space-y-1 print:break-inside-avoid">
      <Badge variant="outline">{cluster.category.replace(/_/g, ' ')}</Badge>
      <p className="text-sm text-foreground">{cluster.description}</p>
      {cluster.example_conversation_ids.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Examples:{' '}
          {cluster.example_conversation_ids.map((id, i) => (
            <span key={id}>
              {i > 0 && ', '}
              <Link
                to={inboxEnabled ? `/inbox/${id}` : `/conversations/${id}`}
                className="text-primary hover:underline"
              >
                {id}
              </Link>
            </span>
          ))}
        </p>
      )}
      {canRun && cluster.example_conversation_ids.length > 0 && (
        <Button
          variant="outline"
          size="sm"
          className="text-xs print:hidden"
          onClick={() =>
            navigate('/admin/conversation-analysis', {
              state: { deepDiveIds: cluster.example_conversation_ids },
            })
          }
        >
          Deep-dive these conversations
        </Button>
      )}
    </div>
  )
}

export function ReportSections({
  run,
  inboxEnabled,
}: {
  run: AnalysisRunDetail
  inboxEnabled: boolean
}) {
  const report = run.report ?? {}
  const verdicts = run.stats.verdicts ?? {}
  const analysis = run.stats.analysis ?? {}
  const clusters = Array.isArray(report.failure_clusters) ? report.failure_clusters : []
  const recommendations = report.recommendations ?? {}
  const prompt = Array.isArray(recommendations.prompt) ? recommendations.prompt : []
  const workflowConfig = Array.isArray(recommendations.workflow_config)
    ? recommendations.workflow_config
    : []
  const extraSections = Object.entries(report).filter(
    ([key, value]) =>
      !KNOWN_REPORT_KEYS.has(key) && typeof value === 'string' && value.trim() !== '',
  ) as [string, string][]

  return (
    <div className="space-y-6">
      {typeof report.executive_summary === 'string' && report.executive_summary.trim() !== '' && (
        <Section title="Executive summary">
          <p className="text-sm text-foreground whitespace-pre-wrap">
            {report.executive_summary}
          </p>
        </Section>
      )}

      <Section title="Outcome breakdown">
        <p className="text-sm text-muted-foreground">
          Analyzed {run.analyzedCount} of {analysis.total_matching ?? run.conversationCount}{' '}
          matching conversations
          {analysis.truncated && <strong> (selection cap — newest first)</strong>}
        </p>
        {typeof report.outcome_breakdown === 'string' && report.outcome_breakdown.trim() !== '' && (
          <p className="text-sm text-foreground whitespace-pre-wrap">
            {report.outcome_breakdown}
          </p>
        )}
        <BreakdownBars
          data={verdicts.outcomes ?? {}}
          colorFor={(key) => OUTCOME_COLORS[key] ?? 'bg-primary'}
        />
        <BreakdownBars data={verdicts.failure_categories ?? {}} colorFor={() => 'bg-primary'} />
      </Section>

      {run.stats.funnel && <FunnelSection funnel={run.stats.funnel} />}

      {run.stats.mission_measures && (
        <MissionMeasuresSection block={run.stats.mission_measures} />
      )}

      {clusters.length > 0 && (
        <Section title="Failure clusters">
          <div className="space-y-2">
            {clusters.map((cluster, i) => (
              <FailureClusterCard key={i} cluster={cluster} inboxEnabled={inboxEnabled} />
            ))}
          </div>
        </Section>
      )}

      {typeof report.escalation_patterns === 'string' && report.escalation_patterns.trim() !== '' && (
        <Section title="Escalation & stall patterns">
          <p className="text-sm text-foreground whitespace-pre-wrap">
            {report.escalation_patterns}
          </p>
        </Section>
      )}

      {typeof report.comparisons === 'string' && report.comparisons.trim() !== '' && (
        <Section title="Per-persona / channel comparison">
          <p className="text-sm text-foreground whitespace-pre-wrap">{report.comparisons}</p>
        </Section>
      )}

      {(prompt.length > 0 || workflowConfig.length > 0) && (
        <Section title="Recommendations">
          <div className="grid gap-4 sm:grid-cols-2">
            {prompt.length > 0 && (
              <div>
                <h3 className="text-xs font-medium text-muted-foreground mb-1">Prompt</h3>
                <ul className="list-disc pl-5 text-sm text-foreground space-y-1">
                  {prompt.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
            {workflowConfig.length > 0 && (
              <div>
                <h3 className="text-xs font-medium text-muted-foreground mb-1">
                  Workflow config
                </h3>
                <ul className="list-disc pl-5 text-sm text-foreground space-y-1">
                  {workflowConfig.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Section>
      )}

      {extraSections.map(([key, value]) => (
        <Section key={key} title={titleize(key)}>
          <p className="text-sm text-foreground whitespace-pre-wrap">{value}</p>
        </Section>
      ))}
    </div>
  )
}
